import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { onboardingSteps } from '../data/onboardingSteps';

const OnboardingContext = createContext({});

export const OnboardingProvider = ({ children }) => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const db = getFirestore();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [createdAgentId, setCreatedAgentId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize onboarding state from Firestore
  useEffect(() => {
    if (userData && userData.hasCompletedOnboarding !== true) {
      setIsOnboardingActive(true);
      
      // Restore onboarding state from Firestore
      const savedStepIndex = userData.onboardingStep;
      const savedAgentId = userData.onboardingAgentId;
      
      if (savedStepIndex !== undefined && savedStepIndex >= 0) {
        setCurrentStepIndex(savedStepIndex);
      }
      
      if (savedAgentId) {
        setCreatedAgentId(savedAgentId);
      }
      
      setInitialized(true);
    } else {
      setIsOnboardingActive(false);
      setInitialized(true);
    }
  }, [userData]);

  const currentStep = onboardingSteps[currentStepIndex];

  // Persist step index to Firestore whenever it changes
  const persistOnboardingState = useCallback(async (stepIndex, agentId = null) => {
    if (!currentUser || !initialized) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const updates = {
        onboardingStep: stepIndex,
      };
      
      if (agentId !== null) {
        updates.onboardingAgentId = agentId;
      }
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error persisting onboarding state:', error);
    }
  }, [currentUser, db, initialized]);

  const nextStep = useCallback(async () => {
    if (currentStepIndex < onboardingSteps.length - 1) {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextStepIndex);

      // Persist to Firestore
      await persistOnboardingState(nextStepIndex, createdAgentId);

      // Navigate to target route if specified
      const nextStepData = onboardingSteps[nextStepIndex];
      if (nextStepData.targetRoute) {
        // Handle dynamic routes with agent ID
        let route = nextStepData.targetRoute;
        if (route.includes(':agentId') && createdAgentId) {
          route = route.replace(':agentId', createdAgentId);
        }
        navigate(route);
      }
    } else {
      // Completed all steps
      await completeOnboarding();
    }
  }, [currentStepIndex, navigate, createdAgentId, persistOnboardingState]);

  const previousStep = useCallback(async () => {
    if (currentStepIndex > 0) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevStepIndex);
      await persistOnboardingState(prevStepIndex, createdAgentId);
    }
  }, [currentStepIndex, createdAgentId, persistOnboardingState]);

  const goToStep = useCallback(async (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < onboardingSteps.length) {
      setCurrentStepIndex(stepIndex);
      await persistOnboardingState(stepIndex, createdAgentId);

      // Navigate to target route if specified
      const stepData = onboardingSteps[stepIndex];
      if (stepData.targetRoute) {
        let route = stepData.targetRoute;
        if (route.includes(':agentId') && createdAgentId) {
          route = route.replace(':agentId', createdAgentId);
        }
        navigate(route);
      }
    }
  }, [navigate, createdAgentId, persistOnboardingState]);

  const completeOnboarding = useCallback(async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        hasCompletedOnboarding: true,
        onboardingStep: null, // Clear onboarding state
        onboardingAgentId: null,
      });
      setIsOnboardingActive(false);
      
      // Navigate to dashboard
      navigate('/admin/default');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  }, [currentUser, db, navigate]);

  const skipOnboarding = useCallback(async () => {
    await completeOnboarding();
  }, [completeOnboarding]);

  const toggleMinimize = useCallback(() => {
    setIsMinimized(!isMinimized);
  }, [isMinimized]);

  // Monitor route changes to extract agent ID from URL
  useEffect(() => {
    if (!isOnboardingActive || !initialized) return;

    // Extract agent ID from agent details route
    const agentMatch = location.pathname.match(/\/admin\/agents\/([^\/]+)/);
    if (agentMatch && agentMatch[1] !== 'new') {
      const agentId = agentMatch[1];
      if (agentId !== createdAgentId) {
        setCreatedAgentId(agentId);
        // Persist the agent ID
        persistOnboardingState(currentStepIndex, agentId);
      }
    }
  }, [location.pathname, isOnboardingActive, createdAgentId, initialized, currentStepIndex, persistOnboardingState]);

  // Method to set created agent ID (can be called from CreateAgentForm)
  const setOnboardingAgentId = useCallback(async (agentId) => {
    setCreatedAgentId(agentId);
    await persistOnboardingState(currentStepIndex, agentId);
  }, [currentStepIndex, persistOnboardingState]);

  const value = {
    isOnboardingActive,
    currentStep,
    currentStepIndex,
    totalSteps: onboardingSteps.length,
    nextStep,
    previousStep,
    goToStep,
    completeOnboarding,
    skipOnboarding,
    isMinimized,
    toggleMinimize,
    createdAgentId,
    setOnboardingAgentId,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

